/**
 * Returns `target` only when it is a safe same-origin relative path. Anything
 * else (absolute URLs, protocol-relative, empty) falls back to `fallback`.
 *
 * Use this to guard a navigation or `redirect()` whenever the target can
 * originate from a user-controlled source (e.g. a `returnUrl` query param), so
 * a crafted link can't turn it into an open redirect.
 *
 * "Starts with / but not //" is not sufficient, because the check runs on the
 * raw string while the browser navigates the WHATWG-parsed one:
 *
 *   - For special schemes the parser treats "\" exactly like "/", so "/\evil.com"
 *     enters special-authority state and lands on https://evil.com.
 *   - The parser strips every ASCII tab, LF and CR before parsing, so "/\t/evil.com"
 *     becomes "//evil.com" after the strip.
 *
 * Both are checked against a stripped copy; the original is what gets returned,
 * since removing those characters cannot make a same-origin path unsafe.
 */
export function safeInternalPath(
  target: string | null | undefined,
  fallback = "/",
): string {
  if (!target) {
    return fallback;
  }

  const parsed = target.replace(/[\t\n\r]/g, "");
  const escapesOrigin = !parsed.startsWith("/") || /^\/[/\\]/.test(parsed);

  return escapesOrigin ? fallback : target;
}
